// src/components/tabs/SplitView.tsx
// Renders the split tree. A group draws its strip and its panes; a split draws
// two children with a draggable divider between them.
//
// Dropping a tab on the edge of a pane creates a split on that side — the
// JetBrains gesture — which is why each group carries invisible edge zones that
// only light up while a tab is being dragged.

import { useCallback, useRef, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { tabsStore } from '@/stores/TabsStore';
import type { PaneNode, TabGroupNode } from '@/features/tabs/types';
import { TabStrip, TAB_DRAG_TYPE } from './TabStrip';
import { TabPane } from './TabPane';

const EDGES = [
  { edge: 'left' as const, direction: 'row' as const, before: true, cls: 'left-0 top-0 bottom-0 w-1/4' },
  { edge: 'right' as const, direction: 'row' as const, before: false, cls: 'right-0 top-0 bottom-0 w-1/4' },
  { edge: 'top' as const, direction: 'column' as const, before: true, cls: 'top-0 left-0 right-0 h-1/4' },
  { edge: 'bottom' as const, direction: 'column' as const, before: false, cls: 'bottom-0 left-0 right-0 h-1/4' },
];

const GroupView = observer(({ group }: { group: TabGroupNode }) => {
  const [dragging, setDragging] = useState(false);
  const [hotEdge, setHotEdge] = useState<string | null>(null);
  const tabs = tabsStore.tabsOf(group.id);
  const isFocused = tabsStore.focusedGroupId === group.id;

  return (
    <div
      className="relative flex flex-col flex-1 min-w-0 min-h-0"
      onFocusCapture={() => {
        if (!isFocused) tabsStore.focusGroup(group.id);
      }}
      onDragEnter={(e) => {
        if (e.dataTransfer.types.includes(TAB_DRAG_TYPE)) setDragging(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget === e.target) {
          setDragging(false);
          setHotEdge(null);
        }
      }}
      onDrop={() => {
        setDragging(false);
        setHotEdge(null);
      }}
    >
      <TabStrip group={group} />

      <div className="relative flex flex-col flex-1 min-h-0">
        {tabs.map((tab) =>
          // A dormant tab keeps its page mounted only while it is within the
          // live budget; past that only its path survives and it remounts.
          tabsStore.isLive(tab.id) ? (
            <TabPane
              key={tab.id}
              tabId={tab.id}
              path={tab.path}
              isActive={group.activeTabId === tab.id}
              isFocused={isFocused}
              width={group.width}
              align={group.align}
            />
          ) : null,
        )}

        {tabs.length === 0 && (
          <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
            &nbsp;
          </div>
        )}

        {dragging &&
          EDGES.map((zone) => (
            <div
              key={zone.edge}
              className={`absolute z-30 ${zone.cls} ${
                hotEdge === zone.edge ? 'bg-primary/20 border-2 border-primary' : ''
              }`}
              onDragOver={(e) => {
                if (!e.dataTransfer.types.includes(TAB_DRAG_TYPE)) return;
                e.preventDefault();
                e.stopPropagation();
                setHotEdge(zone.edge);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const tabId = e.dataTransfer.getData(TAB_DRAG_TYPE);
                setDragging(false);
                setHotEdge(null);
                if (!tabId) return;
                const holder = tabsStore.tabsOf(group.id).some((tb) => tb.id === tabId);
                if (holder) {
                  tabsStore.splitWithTab(group.id, tabId, zone.direction, zone.before);
                } else {
                  // From another pane: land it here first, then split it off.
                  tabsStore.reorderOrMove(tabId, group.id, tabsStore.tabsOf(group.id).length);
                  tabsStore.splitWithTab(group.id, tabId, zone.direction, zone.before);
                }
              }}
            />
          ))}
      </div>
    </div>
  );
});

const SplitContainer = observer(({ node }: { node: Extract<PaneNode, { kind: 'split' }> }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useI18n();
  const isRow = node.direction === 'row';

  const startDrag = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      const container = containerRef.current;
      if (!container) return;

      const onMove = (e: MouseEvent) => {
        const box = container.getBoundingClientRect();
        const ratio = isRow
          ? (e.clientX - box.left) / box.width
          : (e.clientY - box.top) / box.height;
        tabsStore.setRatio(node.id, ratio);
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.cursor = isRow ? 'col-resize' : 'row-resize';
    },
    [isRow, node.id],
  );

  const first = `${node.ratio * 100}%`;
  const second = `${(1 - node.ratio) * 100}%`;

  return (
    <div
      ref={containerRef}
      className={`flex flex-1 min-w-0 min-h-0 ${isRow ? 'flex-row' : 'flex-col'}`}
    >
      <div className="flex min-w-0 min-h-0" style={isRow ? { width: first } : { height: first }}>
        <PaneView node={node.children[0]} />
      </div>
      <div
        // A resize handle really is a slider: it has a current value and
        // bounds, and reporting it as one gets arrow keys and announcements for
        // free. `separator` describes the line, not the control.
        role="slider"
        tabIndex={0}
        aria-orientation={isRow ? 'vertical' : 'horizontal'}
        aria-valuenow={Math.round(node.ratio * 100)}
        aria-valuemin={15}
        aria-valuemax={85}
        aria-label={t('layout.tabs.resize')}
        onMouseDown={startDrag}
        onKeyDown={(e) => {
          const less = isRow ? 'ArrowLeft' : 'ArrowUp';
          const more = isRow ? 'ArrowRight' : 'ArrowDown';
          if (e.key !== less && e.key !== more) return;
          e.preventDefault();
          tabsStore.setRatio(node.id, node.ratio + (e.key === more ? 0.02 : -0.02));
        }}
        className={`flex-none bg-border hover:bg-primary/40 focus:bg-primary/60 focus:outline-none ${
          isRow ? 'w-px cursor-col-resize hover:w-0.5' : 'h-px cursor-row-resize hover:h-0.5'
        }`}
      />
      <div className="flex min-w-0 min-h-0" style={isRow ? { width: second } : { height: second }}>
        <PaneView node={node.children[1]} />
      </div>
    </div>
  );
});

export const PaneView = observer(({ node }: { node: PaneNode }) => {
  if (node.kind === 'group') return <GroupView group={node} />;
  return <SplitContainer node={node} />;
});
